/* eslint-disable */
import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { SessionContext } from '../../context/sessionContext';
import Logger from '../../logger';
import Loader from '../components/common/loader/loader';
import BannerNotification from '../components/common/banner/bannerNotification';
import { StepLayout } from '../components/common/steplayout/StepLayout';
import { useMyVW } from '../../context/myVWContext';
import { useMyVWQueryParams } from '../hooks/useMyVWQueryParams';
import { updateEmail, verifyUpdateEmailIdentity } from '../services/myVW';
import i18n from '../../localization/i18n';
import * as rk from '../../localization/resourceKeys';
import FormField from '../components/common/form-field/formField';
import CaptchaGate from '../components/common/captcha/CaptchaGate';
import { restrictKeyPressHandler } from '../../utilities';
import { isValidAccountNumber, isValidEmail } from '../../utilities';

type PageStep = 'verify' | 'update' | 'success';

const MyVWUpdateEmail: React.FC = () => {
  const navigate = useNavigate();
  const { vwfsToken, partyId, applyAuthState } = useMyVW();
  const { token, brand, country, buildSearch } = useMyVWQueryParams();
  const { sessionDetails } = useContext(SessionContext)!;
  const myVWSessionDetails = { ...sessionDetails, brand, country };
  const sessionToken = vwfsToken || token;

  const [step, setStep] = useState<PageStep>('verify');
  const [usernameInput, setUsernameInput] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [accountNumberInput, setAccountNumberInput] = useState('');
  const [ssnInput, setSsnInput] = useState('');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [accountNumberError, setAccountNumberError] = useState<string | null>(null);
  const [ssnError, setSsnError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [confirmEmailError, setConfirmEmailError] = useState<string | null>(null);
  const [verifyInvalidError, setVerifyInvalidError] = useState<string | null>(null);
  const [updateInvalidError, setUpdateInvalidError] = useState<string | null>(null);
  const [verifyApiError, setVerifyApiError] = useState(false);
  const [updateApiError, setUpdateApiError] = useState(false);
  const [verifyAttempts, setVerifyAttempts] = useState(0);
  const [updateAttempts, setUpdateAttempts] = useState(0);
  const [verifyLocked, setVerifyLocked] = useState(false);
  const [updateLocked, setUpdateLocked] = useState(false);
  const [captchaCompleted, setCaptchaCompleted] = useState(
    process.env.REACT_APP_AWS_ENV === 'dev' || process.env.NODE_ENV === 'test',
  );
  const [loading, setLoading] = useState(false);
  const restrictNumericKeyPress = restrictKeyPressHandler(/^[0-9]*$/i);

  const validateVerify = (): boolean => {
    let valid = true;
    setUsernameError(null);
    setAccountNumberError(null);
    setSsnError(null);

    if (!usernameInput.trim()) {
      setUsernameError(i18n.t(rk.ERROR_API_INVALID_USERNAME));
      valid = false;
    }

    if (!isValidAccountNumber(accountNumberInput)) {
      setAccountNumberError(i18n.t(rk.ERROR_INVALID_ACCNUMBER));
      valid = false;
    }

    if (!/^\d{4}$/.test(ssnInput)) {
      setSsnError(i18n.t(rk.ERROR_SSN_FORMAT));
      valid = false;
    }

    return valid;
  };

  const validateUpdate = (): boolean => {
    let valid = true;
    setEmailError(null);
    setConfirmEmailError(null);

    if (!isValidEmail(email)) {
      setEmailError(i18n.t(rk.ERROR_EMAIL_FORMAT));
      valid = false;
    }
    if (email !== confirmEmail) {
      setConfirmEmailError(i18n.t(rk.ERROR_EMAIL_MATCH));
      valid = false;
    }
    return valid;
  };

  const handleVerify = async () => {
    if (verifyLocked || !validateVerify()) return;
    setVerifyInvalidError(null);
    setVerifyApiError(false);
    setLoading(true);

    const result = await verifyUpdateEmailIdentity(
      usernameInput,
      accountNumberInput,
      ssnInput,
      myVWSessionDetails,
    );

    setLoading(false);

    if (!result.isSuccess) {
      if (result.isLocked || result.loginAttemptCount) {
        const count = result.loginAttemptCount ?? 5;
        setVerifyAttempts(count);
        if (count >= 5 || result.isLocked) {
          setVerifyLocked(true);
          setVerifyInvalidError(null);
        } else {
          setVerifyInvalidError(i18n.t(rk.ERROR_INVALID_INFO));
        }
      } else {
        setVerifyApiError(true);
      }
      Logger.info('myVW update email verify failed', 'Location ==> /myVW/update-email', sessionDetails);
      return;
    }

    setStep('update');
  };

  const handleUpdate = async () => {
    if (updateLocked || !validateUpdate()) return;
    setUpdateInvalidError(null);
    setUpdateApiError(false);
    setLoading(true);
    
    const result = await updateEmail(
      sessionToken,
      email,
      accountNumberInput,
      ssnInput,
      partyId ?? undefined,
      myVWSessionDetails,
    );

    setLoading(false);

    if (!result.isSuccess) {
      if (result.isLocked || result.loginAttemptCount) {
        const count = result.loginAttemptCount ?? 5;
        setUpdateAttempts(count);
        if (count >= 5 || result.isLocked) {
          setUpdateLocked(true);
          setUpdateInvalidError(null);
        } else {
          setUpdateInvalidError(i18n.t(rk.ERROR_INVALID_INFO));
        }
      } else {
        setUpdateApiError(true);
      }
      Logger.info('myVW update email submit failed', 'Location ==> /myVW/update-email', sessionDetails);
      return;
    }

    applyAuthState({
      email,
      accountNumber: accountNumberInput,
    });
    Logger.info('myVW update email success', 'Location ==> /myVW/update-email', sessionDetails);
    setStep('success');
  };

  if (step === 'success') {
    return (
      <StepLayout
        title={<h3 className='u-mb-xsmall'>{i18n.t(rk.TITLE_MYVW_UPDATE_EMAIL_SUCCESS)}</h3>}
        secondaryAction={{
          label: i18n.t(rk.BUTTON_CONTINUE),
          onClick: () => navigate('/myVW/mfa' + buildSearch(), { replace: true }),
          testId: 'update-email-success-continue',
        }}
      >
        <p className="u-mt" data-testid="update-email-success-text">
          {i18n.t(rk.TEXT_MYVW_UPDATE_EMAIL_SUCCESS)}
        </p>
      </StepLayout>
    );
  }

  if (step === 'verify') {
    return (
      <StepLayout
        title={<h3 className='u-mb-xsmall'>{i18n.t(rk.TITLE_MYVW_UPDATE_EMAIL)}</h3>}
        primaryAction={{
          label: i18n.t(rk.BUTTON_CANCEL),
          onClick: () => navigate(-1),
          testId: 'update-email-verify-cancel',
        }}
        secondaryAction={{
          label: i18n.t(rk.BUTTON_CONTINUE),
          onClick: handleVerify,
          disabled: loading || verifyLocked || !usernameInput || !accountNumberInput || !ssnInput,
          testId: 'update-email-verify-continue',
        }}
      >
        <Loader isLoading={loading} />
        {verifyLocked && (
          <div className="u-mt" data-testid="update-email-verify-lockout-banner">
            <BannerNotification title={`${i18n.t(rk.TITLE_LOCKOUT_ERROR)}`} notificationStyle="error">
              {i18n.t(rk.TEXT_LOCKOUT_ERROR_MFA) as any}
            </BannerNotification>
          </div>
        )}
        {verifyAttempts >= 2 && verifyAttempts < 5 && (
          <div className="u-mt" data-testid="update-email-verify-warning-banner">
            <BannerNotification
              title={`${i18n.t(rk.TITLE_LOCKOUT_WARNING1)} ${5 - verifyAttempts}
             ${verifyAttempts === 4 ? i18n.t(rk.TITLE_LOCKOUT_WARNING3) : i18n.t(rk.TITLE_LOCKOUT_WARNING2)}`}
              notificationStyle="warning"
            >
              {i18n.t(rk.TEXT_LOCKOUT_WARNING_VERIFY) as any}
            </BannerNotification>
          </div>
        )}
        {verifyApiError && (
          <div className="u-mt" data-testid="update-email-verify-api-error">
            <BannerNotification notificationStyle="error">
              {i18n.t(rk.ERROR_API_SERVICE_DOWN) as any}
            </BannerNotification>
          </div>
        )}
        <p className="u-mt">{i18n.t(rk.TEXT_MYVW_UPDATE_EMAIL)}</p>
        <p className="required-text">{i18n.t(rk.TEXT_ALL_FIELDS_REQUIRED)}</p>
        <FormField
          id="verify-username"
          label={i18n.t(rk.LABEL_USERNAME)}
          type="text"
          testid="verify-username-input"
          value={usernameInput}
          disabled={verifyLocked}
          onChange={(e) => {
            setUsernameInput(e.target.value);
            setUsernameError(null);
            setVerifyInvalidError(null);
          }}
          fieldErrors={(usernameError || undefined) as unknown as string[] | undefined}
        />
        <FormField
          id="verify-ssn"
          label={i18n.t(rk.LABEL_SSN_LAST4)}
          type="text"
          testid="verify-ssn-input"
          value={ssnInput}
          disabled={verifyLocked}
          onKeyPress={restrictNumericKeyPress}
          onCopy={(e) => {
            e.preventDefault();
            return false;
          }}
          onPaste={(e) => {
            e.preventDefault();
            return false;
          }}
          onBlur={() => {
            if (ssnInput && !/^\d{4}$/.test(ssnInput)) {
              Logger.info(
                "User entered invalid ssn on update email flow",
                "Location ==> /Login",
                sessionDetails,
                "",
                "",
                ""
              );
            }
          }}
          onChange={(e) => {
            if (e.target.value.length > 4) return;
            setSsnInput(e.target.value);
            setSsnError(null);
            setVerifyInvalidError(null);
          }}
          fieldErrors={(ssnError || undefined) as unknown as string[] | undefined}
          hint={i18n.t(rk.TEXT_SSN_LAST4_HINT)}
        />
        <FormField
          id="verify-account-number"
          label={i18n.t(rk.LABEL_ACCOUNT_NUMBER)}
          type="text"
          testid="verify-account-number-input"
          value={accountNumberInput}
          disabled={verifyLocked}
          onKeyPress={restrictNumericKeyPress}
          onChange={(e) => {
            if (e.target.value.length > 10) return;
            setAccountNumberInput(e.target.value);
            setAccountNumberError(null);
            setVerifyInvalidError(null);
          }}
          fieldErrors={
            (accountNumberError || verifyInvalidError || undefined) as unknown as string[] | undefined
          }
          hint={i18n.t(rk.TEXT_ACCOUNT_NUM_HINT)}
        />

      </StepLayout>
    );
  }

  return (
    <StepLayout
      title={<h3 className='u-mb-xsmall'>{i18n.t(rk.TITLE_MYVW_UPDATE_EMAIL)}</h3>}
      primaryAction={{
        label: i18n.t(rk.BUTTON_CANCEL),
        onClick: () => setStep('verify'),
        testId: 'update-email-update-cancel',
      }}
      secondaryAction={{
        label: i18n.t(rk.BUTTON_CONTINUE),
        onClick: handleUpdate,
        disabled:
          !email ||
          !confirmEmail ||
          loading ||
          !captchaCompleted ||
          updateLocked,
        testId: 'update-email-update-continue',
      }}
    >
      <Loader isLoading={loading} />
      {updateLocked && (
        <div className="u-mt" data-testid="update-email-lockout-banner">
          <BannerNotification title={`${i18n.t(rk.TITLE_LOCKOUT_ERROR)}`} notificationStyle="error">
            {i18n.t(rk.TEXT_LOCKOUT_ERROR_MFA) as any}
          </BannerNotification>
        </div>
      )}
      {updateAttempts >= 2 && updateAttempts < 5 && (
        <div className="u-mt" data-testid="update-email-warning-banner">
          <BannerNotification
            title={`${i18n.t(rk.TITLE_LOCKOUT_WARNING1)} ${5 - updateAttempts}
             ${updateAttempts === 4 ? i18n.t(rk.TITLE_LOCKOUT_WARNING3) : i18n.t(rk.TITLE_LOCKOUT_WARNING2)}`}
            notificationStyle="warning"
          >
            {i18n.t(rk.TEXT_LOCKOUT_WARNING_VERIFY) as any}
          </BannerNotification>
        </div>
      )}
      {updateApiError && (
        <div className="u-mt" data-testid="update-email-api-error">
          <BannerNotification notificationStyle="error">
            {i18n.t(rk.ERROR_API_SERVICE_DOWN) as any}
          </BannerNotification>
        </div>
      )}
      <p className="u-mt">{i18n.t(rk.TEXT_MYVW_UPDATE_EMAIL)}</p>
      <p className="required-text">{i18n.t(rk.TEXT_ALL_FIELDS_REQUIRED)}</p>
      <FormField
        id="new-email"
        label={i18n.t(rk.LABEL_NEW_EMAIL_ADDRESS)}
        type="email"
        testid="new-email-input"
        errorTestid="new-email-error"
        value={email}
        disabled={updateLocked}
        onChange={(e) => {
          setEmail(e.target.value);
          setEmailError(null);
          setUpdateInvalidError(null);
        }}
        fieldErrors={(emailError || updateInvalidError || undefined) as unknown as string[] | undefined}
      />
      <FormField
        id="confirm-email"
        label={i18n.t(rk.LABEL_CONFIRM_EMAIL_ADDRESS)}
        type="email"
        testid="confirm-email-input"
        errorTestid="confirm-email-error"
        value={confirmEmail}
        disabled={updateLocked}
        onChange={(e) => {
          setConfirmEmail(e.target.value);
          setConfirmEmailError(null);
        }}
        onCopy={(e) => { e.preventDefault(); return false; }}
        onPaste={(e) => { e.preventDefault(); return false; }}
        fieldErrors={(confirmEmailError || undefined) as unknown as string[] | undefined}
      />
      <CaptchaGate
        onStatusChange={setCaptchaCompleted}
        widgetTestId="myvw-captcha-widget"
        errorTestId="myvw-captcha-error"
      />
    </StepLayout>
  );
};

export default MyVWUpdateEmail;
